import React, { useState, useEffect } from "react";
import { FiSave, FiTrash2 } from "react-icons/fi";
import type {
  MlQuery,
  CreateMlQueryData,
  UpdateMlQueryData,
} from "../../../shared/services/apis/mlQueries";
import type { Tenant } from "../../../shared/types";
import Dialog from "../../../shared/ui/dialog";
import defaultMlQuery from "../pages/defaultMlQuery.json";
interface MlQueryModelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    data: Omit<CreateMlQueryData, "category"> | UpdateMlQueryData,
    category?: string
  ) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onDeleteAll?: (tenantId: string, category?: string) => Promise<void>;
  riskParameter: MlQuery | null;
  tenants: Tenant[];
  selectedTenant?: Tenant | null;
  isLoading: boolean;
  selectedCategory?: string; // New prop for category
}

const MlQueryModel: React.FC<MlQueryModelModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  onDeleteAll,
  riskParameter,
  tenants,
  selectedTenant,
  isLoading,
  selectedCategory = "RISK_PARAMETERS",
}) => {
  const [formData, setFormData] = useState({
    tenantId: "",
    query: "",
    description: "",
    type: "",
    category: selectedCategory,
    riskType: "",
    priority: 1,
  });

  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  // Get categories and templates from JSON file
  const categoriesData = defaultMlQuery.categories;
  const availableCategories = Object.keys(categoriesData);
  const currentCategoryData =
    categoriesData[formData.category as keyof typeof categoriesData];
  const defaultQueries = currentCategoryData?.templates || [];

  const handleTemplateSelect = (templateId: string) => {
    const template = defaultQueries.find((q: any) => q.id === templateId);
    console.log("Selected template:", template);
    if (template) {
      setFormData({
        ...formData,
        description: template.description,
        query: template.query,
        type: template.type,
        riskType: template.riskType || "",
        priority: template.priority || 1,
      });
    }
  };

  const handleCreateAllTemplates = async () => {
    if (!formData.tenantId) {
      alert("Please select a tenant first");
      return;
    }

    try {
      // Create all default templates for the selected tenant in the current category
      for (const template of defaultQueries) {
        await onSave(
          {
            tenantId: formData.tenantId,
            description: template.description,
            query: template.query,
            type: template.type,
            riskType: template.riskType,
            priority: template.priority,
          },
          formData.category
        );
      }
      // Close modal after creating all
      onClose();
    } catch (error) {
      console.error("Failed to create all templates:", error);
    }
  };

  useEffect(() => {
    if (riskParameter) {
      setFormData({
        tenantId: riskParameter.tenantId,
        query: riskParameter.query,
        description: riskParameter.description,
        type: riskParameter.type,
        category: riskParameter.category || selectedCategory,
        riskType: (riskParameter as any).riskType || "",
        priority: (riskParameter as any).priority || 1,
      });
    } else {
      setFormData({
        tenantId: selectedTenant?.id || "",
        query: "",
        description: "",
        type: "",
        category: selectedCategory,
        riskType: "",
        priority: 1,
      });
    }
  }, [riskParameter, selectedTenant, selectedCategory]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // If editing an existing risk parameter, set status to PENDING and response to undefined
      const submitData = riskParameter
        ? {
            ...formData,
            responseStatus: "PENDING" as const,
            response: undefined,
          }
        : formData;

      // Remove category from submit data as it's handled separately
      const { category, ...dataToSubmit } = submitData;

      await onSave(dataToSubmit, formData.category);
      // Don't close here - let the parent component handle closing after successful save
    } catch (error) {
      console.error("Failed to save risk parameter:", error);
    }
  };

  const handleDelete = async () => {
    if (riskParameter && onDelete) {
      try {
        await onDelete(riskParameter.id);
        // Don't close here - let parent handle closing
      } catch (error) {
        console.error("Failed to delete risk parameter:", error);
      }
    }
  };

  const handleDeleteAll = async () => {
    if (formData.tenantId && onDeleteAll) {
      try {
        await onDeleteAll(formData.tenantId, formData.category);
        setShowDeleteAllConfirm(false);
        onClose();
      } catch (error) {
        console.error("Failed to delete all risk parameters:", error);
      }
    }
  };

  if (!isOpen) return null;

  console.log("Rendering RiskParameterModal with formData:", formData);
  return (
    <Dialog onClose={onClose} isOpen={isOpen} title="Risk Parameter">
      <div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="tenant-select" className="block text-sm font-medium text-gray-700 mb-2">
              Tenant *
            </label>
            <select
              id="tenant-select"
              value={formData.tenantId}
              onChange={(e) =>
                setFormData({ ...formData, tenantId: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={!!riskParameter || !!selectedTenant} // Disable if editing or creating for specific tenant
            >
              <option value="">Select a tenant</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="category-select" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category-select"
              value={formData.category}
              onChange={(e) => {
                const newCategory = e.target.value;
                setFormData({
                  ...formData,
                  category: newCategory,
                  // Reset template-related fields when category changes
                  description: "",
                  query: "",
                  type: "",
                  riskType: "",
                  priority: 1,
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={!!riskParameter} // Disable if editing existing parameter
            >
              {availableCategories.map((categoryKey) => {
                const categoryData =
                  categoriesData[categoryKey as keyof typeof categoriesData];
                return (
                  <option key={categoryKey} value={categoryKey}>
                    {categoryData.name}
                  </option>
                );
              })}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {currentCategoryData?.description}
            </p>
          </div>

          {/* Template Selector - only show when creating new */}
          {!riskParameter && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Templates - {currentCategoryData?.name}
              </label>
              <select
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
                value=""
              >
                <option value="">Select a template (optional)</option>
                {defaultQueries.map((template: any) => (
                  <option key={template.id} value={template.id}>
                    {template.description} ({template.type})
                  </option>
                ))}
              </select>

              {/* Create All Templates Button */}
              {defaultQueries.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={handleCreateAllTemplates}
                    disabled={isLoading || !formData.tenantId}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-green-300 shadow-sm text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSave className="h-4 w-4 mr-2" />
                    Create All {defaultQueries.length}{" "}
                    {currentCategoryData?.name} Templates
                  </button>

                  <div className="text-xs text-gray-500 mt-2 text-center">
                    This will create all {defaultQueries.length} default{" "}
                    {currentCategoryData?.name.toLowerCase()} parameters for the
                    selected tenant
                  </div>
                </>
              )}

              {defaultQueries.length === 0 && (
                <div className="text-xs text-gray-400 text-center py-2">
                  No templates available for this category
                </div>
              )}
            </div>
          )}

          <div>
            <label htmlFor="description-input" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <input
              id="description-input"
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Highly competitive industries"
              required
            />
          </div>

          <div>
            <label htmlFor="query-textarea" className="block text-sm font-medium text-gray-700 mb-2">
              Query *
            </label>
            <textarea
              id="query-textarea"
              value={formData.query}
              onChange={(e) =>
                setFormData({ ...formData, query: e.target.value })
              }
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Strategic Risks: The company operates in highly competitive industries..."
              required
            />
          </div>

          <div>
            <label htmlFor="type-select" className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              id="type-select"
              value={formData.type}
              onChange={(e) =>
                setFormData({ ...formData, type: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Type</option>
              <option value="% / Number">% / Number</option>
              <option value="Text / Qualitative">Text / Qualitative</option>
              <option value="Text and Numbers">Text and Numbers</option>
              <option value="Informational">Informational</option>
              <option value="Yes/No">Yes/No</option>
              <option value="Date">Date</option>
            </select>
          </div>

          {/* Show Risk Type field for BANK_RISK_SCORE category */}
          {formData.category === 'BANK_RISK_SCORE' && (
            <div>
              <label htmlFor="risk-type-select" className="block text-sm font-medium text-gray-700 mb-2">
                Risk Type
              </label>
              <select
                id="risk-type-select"
                value={formData.riskType}
                onChange={(e) =>
                  setFormData({ ...formData, riskType: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Risk Type</option>
                <option value="INDUSTRY">Industry</option>
                <option value="BUSINESS">Business</option>
                <option value="FINANCIAL">Financial</option>
                <option value="MANAGEMENT">Management</option>
              </select>
            </div>
          )}

          <div>
            <label htmlFor="priority-input" className="block text-sm font-medium text-gray-700 mb-2">
              Priority
            </label>
            <input
              id="priority-input"
              type="number"
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: Number.parseInt(e.target.value) || 1 })
              }
              min="1"
              max="10"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter priority (1-10)"
            />
          </div>
        </form>

        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            <FiSave className="h-4 w-4 mr-2" />
            {isLoading ? "Saving..." : "Save"}
          </button>

          {riskParameter && onDelete && (
            <button
              type="button"
              onClick={handleDelete}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              <FiTrash2 className="h-4 w-4 mr-2" />
              Delete
            </button>
          )}

          {!riskParameter && onDeleteAll && formData.tenantId && (
            <button
              type="button"
              onClick={() => setShowDeleteAllConfirm(true)}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-red-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              <FiTrash2 className="h-4 w-4 mr-2" />
              Delete All Existing
            </button>
          )}

          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Delete All Confirmation Dialog */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiTrash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete All Risk Parameters
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete all existing risk
                        parameters for this tenant? This action cannot be
                        undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleDeleteAll}
                  disabled={isLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Delete All
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteAllConfirm(false)}
                  disabled={isLoading}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default MlQueryModel;
